import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { Warehouse } from "../../models/Warehouse";
import {
  Product,
  ProductType,
  ProductCategory,
  ProductReservation,
} from "../../models/Product";
import { Partner } from "../../models/Partner";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllPartners } from "../../services/PartnerService";
import {
  createProductReservation,
  getAllProductReservations,
  getOneProductReservation,
  updateOneProductReservation,
} from "../../services/ProductReservationService";
import "./NewProductReservationForm.scss";

export interface NewProductReservationFormProps {
  show?: string;
  productReservation?: ProductReservation;
  reservationType?: ProductType;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
  refreshingData?: () => Promise<void> | void;
}

export const NewProductReservationForm = (
  p: NewProductReservationFormProps,
) => {
  const {
    productReservation,
    reservationType,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonResetStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const toast = useRef<any>(null);

  const [visible, setVisible] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);

  const [productCategory, setProductCategory] = useState<ProductCategory>();
  const [maxAmount, setMaxAmount] = useState<number>(0); //maksimalno dostupna kolicina za selektovani magacin i selektovanu robu
  const [newProductsReproReservation, setNewProductsReproReservation] =
    useState<Product[]>([]);
  const [
    newProductsReproRemainReservation,
    setNewProductsReproRemainReservation,
  ] = useState<Product[]>([]);
  const [productsDataTable, setProductsDataTable] = useState<
    {
      id: string;
      name: string;
      amount: string;
      amountRemain: string;
      measuringUnit: string;
    }[]
  >([]);

  const defaultExpiry = useMemo(() => {
    // komercijalista -> 7 dana, ostali -> 365
    const days = activeUser?.role?.name === RoleType.Commercialist ? 7 : 365;
    return new Date(new Date().setDate(new Date().getDate() + days));
  }, []);

  const [newProductReservation, setNewProductReservation] =
    useState<ProductReservation>({
      id: productReservation?.id ?? "",
      warehouse: productReservation?.warehouse ?? ({} as Warehouse),
      partner: productReservation?.partner ?? ({} as Partner),
      product: productReservation?.product ?? ({} as Product),
      type: reservationType,
      category: productReservation?.category ?? undefined,
      amount: productReservation?.amount ?? undefined,
      reservationNumberFormatted:
        productReservation?.reservationNumberFormatted ?? "",
      date: productReservation?.date ?? new Date(),
      expiryDate: productReservation?.expiryDate
        ? new Date(productReservation.expiryDate)
        : defaultExpiry,
      isNotification: productReservation?.isNotification ?? undefined,
      note: productReservation?.note ?? "",
      repromaterialProductsReservationJSON:
        productReservation?.repromaterialProductsReservationJSON,
      reproProductsRemainingReservationJSON:
        productReservation?.reproProductsRemainingReservationJSON,
      requestId: productReservation?.requestId,
    });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, paResp, prResp, pcResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllProducts(),
      getAllProductCategories(),
    ]);

    [wResp, paResp, prResp, pcResp].forEach(ensureSuccess);

    const wh: Warehouse[] = wResp?.data?.result ?? [];
    const partners: Partner[] = paResp?.data?.result ?? [];
    const products: Product[] = prResp?.data?.result ?? [];
    const categories: ProductCategory[] = pcResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setProductsApi(products);
    setProductCategoriesApi(categories);

    // partneri: ako je Commercialist -> filtriraj
    if (activeUser?.role?.name === RoleType.Commercialist) {
      const fullName =
        `${activeUser?.firstName ?? ""} ${activeUser?.lastName ?? ""}`.trim();
      setPartnersApi(
        partners.filter((x: any) => x?.commercialist1 === fullName),
      );
    } else {
      setPartnersApi(partners);
    }

    return { wh, partners, products, categories };
  };

  useEffect(() => {
    const run = async () => {
      try {
        await refreshLookups();
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju šifarnika")),
          life: 3500,
        });
        setWarehousesApi([]);
        setPartnersApi([]);
        setProductsApi([]);
        setProductCategoriesApi([]);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!productReservation) return;
    reset();
    setNewProductReservation((prev) => ({
      ...prev,
      ...productReservation,
      expiryDate: productReservation.expiryDate
        ? new Date(productReservation.expiryDate)
        : prev.expiryDate,
    }));
    setProductCategory(productReservation.product?.category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productReservation]);

  useEffect(() => {
    if (productReservation?.id) return;
    // kad nema selektovane rezervacije -> pripremi formu za create
    reset();
    setProductCategory(undefined);
    setNewProductsReproReservation([]);
    setNewProductsReproRemainReservation([]);
    setNewProductReservation(makeEmptyReservation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productReservation?.id]);

  useEffect(() => {
    if (show) setVisible(true);
  }, [show]);

  useEffect(() => {
    const run = async () => {
      const id = String(newProductReservation.id ?? "").trim();
      if (!id) return;

      try {
        const resp = await getOneProductReservation(id);
        ensureSuccess(resp);

        const pr = resp?.data?.result as ProductReservation;
        if (pr?.expiryDate) (pr as any).expiryDate = new Date(pr.expiryDate);

        setNewProductReservation(pr);

        const reproJson = pr?.repromaterialProductsReservationJSON;
        const remainJson = pr?.reproProductsRemainingReservationJSON;

        if (reproJson) setNewProductsReproReservation(JSON.parse(reproJson));
        if (remainJson)
          setNewProductsReproRemainReservation(JSON.parse(remainJson));

        setProductCategory(pr?.product?.category);
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju rezervacije")),
          life: 3500,
        });
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProductReservation.id]);

  useEffect(() => {
    const run = async () => {
      if (
        newProductReservation.type === ProductType.MercantileGoods &&
        newProductReservation.warehouse?.id &&
        newProductReservation.product?.id
      ) {
        await calculateMaxAmount();
      } else {
        setMaxAmount(0);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newProductReservation.warehouse?.id,
    newProductReservation.product?.id,
    newProductReservation.type,
    businessYear,
    productsApi.length,
  ]);

  useEffect(() => {
    const rows: any[] = [];
    newProductsReproReservation?.forEach((product: any, index: number) => {
      rows.push({
        id: product.id,
        name: product.name,
        amount: product.amount as string,
        amountRemain: newProductsReproRemainReservation?.[index]
          ?.amount as string,
        measuringUnit: product.measuringUnit,
      });
    });
    setProductsDataTable(rows);
  }, [newProductsReproReservation, newProductsReproRemainReservation]);

  const calculateMaxAmount = async () => {
    // maksimalna dostupna samo za merkantilu
    if (newProductReservation.type !== ProductType.MercantileGoods) {
      setMaxAmount(0);
      return;
    }

    const warehouse = newProductReservation.warehouse;
    const product = newProductReservation.product;

    if (!warehouse?.id || !product?.id) {
      setMaxAmount(0);
      return;
    }

    const today = new Date();
    const dateFrom = new Date(businessYear, 0, 1); // 1.1.
    const dateTo =
      businessYear === today.getFullYear()
        ? today
        : new Date(businessYear, 11, 31); // 31.12. ako nije tekuća godina

    // 1) Ulazi, izlazi i rezervacije sa servera
    const [inputsResp, outputsResp, reservationsResp]: [
      AxiosResponse,
      AxiosResponse,
      AxiosResponse,
    ] = await Promise.all([
      // merkantila – prijemnice
      getAllDocuments(true, true, dateFrom, dateTo, false, warehouse.id),
      // merkantila – otpremnice
      getAllDocuments(true, false, dateFrom, dateTo, false, warehouse.id),
      // sve rezervacije u opsegu (filtriraćemo po tipu i robi)
      getAllProductReservations({
        year: businessYear,
        dateFrom,
        dateTo,
      }),
    ]);

    const mercantileInputs = inputsResp.data.result ?? [];
    const mercantileOutputs = outputsResp.data.result ?? [];
    const reservations = reservationsResp.data.result ?? [];

    let newMaxAmount = 0;

    const matchesProductFromDoc = (doc: any) => {
      const productFromList =
        productsApi.find((p) =>
          doc.productId
            ? p.id?.toString() === doc.productId.toString()
            : p.name === doc.productName,
        ) || undefined;

      const docProductId =
        productFromList?.id ?? doc.productId ?? doc.productName;

      return (
        docProductId?.toString() === product.id?.toString() ||
        doc.productName === product.name
      );
    };

    // 2) Ulazi
    mercantileInputs.forEach((doc: any) => {
      if (doc?.isDocumentFinished !== true) return;
      if (!matchesProductFromDoc(doc)) return;

      const netto = doc.netto ?? 0;
      newMaxAmount += netto;
    });

    // 3) Izlazi
    mercantileOutputs.forEach((doc: any) => {
      if (doc?.isDocumentFinished !== true) return;
      if (!matchesProductFromDoc(doc)) return;

      const netto = doc.netto ?? 0;
      newMaxAmount -= netto;
    });

    // 4) Rezervacije (samo važeće, samo za istu robu i magacin)
    const nowTs = Date.now();

    reservations.forEach((res: any) => {
      if (res.type !== ProductType.MercantileGoods) return;

      if (!res.warehouse || res.warehouse.id !== warehouse.id) return;
      if (!res.product || res.product.id !== product.id) return;

      const expiryTs = res.expiryDate ? new Date(res.expiryDate).getTime() : 0;

      // Uzimamo u obzir samo rezervacije koje još važe
      if (expiryTs <= nowTs) return;

      const amount = res.amount ?? 0;
      newMaxAmount -= amount;
    });

    setMaxAmount(newMaxAmount);
  };

  const makeEmptyReservation = (): ProductReservation => ({
    id: "",
    warehouse: {} as Warehouse,
    partner: {} as Partner,
    product: {} as Product,
    type: reservationType,
    category: undefined,
    amount: undefined,
    reservationNumberFormatted: "",
    date: new Date(),
    expiryDate: defaultExpiry,
    isNotification: undefined,
    note: "",
    repromaterialProductsReservationJSON: undefined,
    reproProductsRemainingReservationJSON: undefined,
    requestId: undefined,
  });

  const openModal = () => {
    // ako je create (nema id) -> resetuj state
    if (!productReservation?.id) {
      reset();
      setProductCategory(undefined);
      setNewProductsReproReservation([]);
      setNewProductsReproRemainReservation([]);
      setNewProductReservation(makeEmptyReservation());
    }
    setVisible(true);
  };

  const handleNewProductReservationForm = (prop: string, value: any) => {
    setNewProductReservation((newProductReservation) => ({
      ...newProductReservation,
      [prop]: value,
    }));
  };

  const handleWarehouseChange = (_prop: string, value: any) => {
    const wh = warehousesApi.find((x) => x.id == value) as Warehouse;
    setNewProductReservation((s) => ({
      ...s,
      warehouse: wh ?? ({} as Warehouse),
    }));
  };

  const handlePartnerChange = (_prop: string, value: any) => {
    const pa = partnersApi.find((x) => x.id == value) as Partner;
    setNewProductReservation((s) => ({ ...s, partner: pa ?? ({} as Partner) }));
  };

  const handleProductChange = (_prop: string, value: any) => {
    const pr = productsApi.find((x) => x.id == value) as Product;
    setNewProductReservation((s) => ({ ...s, product: pr ?? ({} as Product) }));
  };

  const handleProductCategoryChange = (_prop: string, value: any) => {
    const cat = productCategoriesApi.find(
      (x) => x.id == value,
    ) as ProductCategory;
    setProductCategory(cat);
  };

  const onSubmit = async () => {
    if (
      newProductReservation.type === ProductType.MercantileGoods &&
      newProductReservation.amount &&
      maxAmount < (newProductReservation?.amount ?? 0)
    ) {
      toast.current.show({
        severity: "error",
        summary: t(`Neodgovarajuća količina`),
        detail: t(
          `Uneta količina rezervacije je veća od maksimalno dostupne količine u selektovanom magacinu. Maksimalno dostupna količina za izabrani proizvod je ${maxAmount}`,
        ),
        sticky: true,
      });
      return;
    }
    try {
      setProcessing(true);

      const payload: any = {
        ...newProductReservation,
        repromaterialProductsReservationJSON: JSON.stringify(
          newProductsReproReservation ?? [],
        ),
        reproProductsRemainingReservationJSON: JSON.stringify(
          newProductsReproReservation ?? [],
        ),
        category: productCategory ?? newProductReservation.category,
      };

      if (payload?.id) {
        const resp = await updateOneProductReservation(payload.id, payload);
        ensureSuccess(resp);
      } else {
        const resp = await createProductReservation(payload);
        ensureSuccess(resp);
      }

      toast.current?.show({
        severity: "success",
        detail: t("Uspešno sačuvano"),
        life: 3000,
      });

      // zatvori + reset + refresh tabele
      setVisible(false);
      handleResetModal();
      await Promise.resolve(refreshingData?.());
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri čuvanju rezervacije")),
        life: 3500,
      });
    } finally {
      setProcessing(false);
    }
  };

  const onError = () => {
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    setNewProductReservation({
      id: "",
      warehouse: {} as Warehouse,
      partner: {} as Partner,
      product: {} as Product,
      type: reservationType,
      category: undefined,
      amount: undefined,
      date: new Date(),
      expiryDate: undefined,
      isNotification: undefined,
      note: "",
    });
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 18 }}></i>
      </span>
      {newProductReservation.id === "" ? (
        <span>{t("Kreiranje rezervacije")}</span>
      ) : newProductReservation.type === ProductType.MercantileGoods ? (
        <span>{`${t("Ažuriranje rezervacije ")}${
          newProductReservation.reservationNumberFormatted
        }`}</span>
      ) : (
        <span>{t("Pregled rezervacije")}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      {newProductReservation.type === ProductType.MercantileGoods && (
        <ButtonInput
          labelText={t("Kreiraj")}
          onButtonClick={handleSubmit(onSubmit, onError)}
          addStyle={addButtonSaveStyle}
          disabled={
            processing ||
            !(
              activeUser.role?.name === RoleType.Superadmin ||
              activeUser.role?.name === RoleType.Administrator ||
              activeUser.role?.name === RoleType.Commercialist ||
              activeUser.role?.name === RoleType.CommercialistII
            )
          }
        />
      )}
      {newProductReservation.type === ProductType.MercantileGoods && (
        <ButtonInput
          labelText={t("Resetuj")}
          onButtonClick={handleResetModal}
          addStyle={addButtonResetStyle}
          disabled={
            processing ||
            !(
              activeUser.role?.name === RoleType.Superadmin ||
              activeUser.role?.name === RoleType.Administrator ||
              activeUser.role?.name === RoleType.Commercialist ||
              activeUser.role?.name === RoleType.CommercialistII
            )
          }
        />
      )}
    </div>
  );

  const nameOfProduct = (rowData: any) => {
    return productsApi.find((item) => item.id == rowData.name)?.name as string;
  };

  const measuringUnitOfProduct = (rowData: any) => {
    return productsApi.find((item) => item.id == rowData.name)
      ?.measuringUnit as string;
  };

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={openModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visible ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-product-reservation-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={() => {
              setVisible(false);
            }}
          >
            <div className="new-product-reservation-form-body">
              <DropdownInput
                id="warehouse"
                value={newProductReservation.warehouse?.id as string}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehousesApi.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator ||
                    activeUser.role?.name === RoleType.Commercialist ||
                    activeUser.role?.name === RoleType.CommercialistII
                  )
                }
              />
              <DropdownInput
                id="partner"
                value={newProductReservation.partner?.id as string}
                onValueChange={handlePartnerChange}
                optionValue="id"
                optionLabel="name"
                options={partnersApi}
                filter={true}
                labelText={`${t("Kupac robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator ||
                    activeUser.role?.name === RoleType.Commercialist ||
                    activeUser.role?.name === RoleType.CommercialistII
                  )
                }
              />
              <DropdownInput
                id="type"
                value={newProductReservation.type as string}
                onValueChange={handleNewProductReservationForm}
                options={[
                  {
                    label: t("Merkantila"),
                    value: ProductType.MercantileGoods,
                  },
                  {
                    label: t("Repromaterijal"),
                    value: ProductType.RawMaterial,
                  },
                ]}
                filter={false}
                labelText={`${t("Tip robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={true}
              />
              {newProductReservation.type === ProductType.MercantileGoods && (
                <DropdownInput
                  id="productCategory"
                  value={productCategory?.id}
                  onValueChange={handleProductCategoryChange}
                  optionValue="id"
                  optionLabel="name"
                  options={productCategoriesApi.filter(
                    (item) => item.type === newProductReservation.type,
                  )}
                  filter={false}
                  labelText={`${t("Vrsta robe")} *`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newProductReservation.type === ProductType.MercantileGoods
                      ? { required: true }
                      : {}
                  }
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator ||
                      activeUser.role?.name === RoleType.Commercialist ||
                      activeUser.role?.name === RoleType.CommercialistII
                    )
                  }
                />
              )}
              {newProductReservation.type === ProductType.MercantileGoods && (
                <DropdownInput
                  id="product"
                  value={newProductReservation.product.id as string}
                  onValueChange={handleProductChange}
                  optionValue="id"
                  optionLabel="name"
                  options={productsApi.filter(
                    (item) => item.category?.id === productCategory?.id,
                  )}
                  filter={false}
                  labelText={`${t("Roba")} *`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator ||
                      activeUser.role?.name === RoleType.Commercialist ||
                      activeUser.role?.name === RoleType.CommercialistII
                    )
                  }
                />
              )}
              {newProductReservation.type === ProductType.MercantileGoods && (
                <TextInput
                  id="amount"
                  value={newProductReservation.amount ?? ""}
                  onValueChange={handleNewProductReservationForm}
                  labelText={`${t("Količina")} *`}
                  placeholderText={t("Unesite količinu rezervacije")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /^\d+$/,
                      message: "Please enter a number",
                    },
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator ||
                      activeUser.role?.name === RoleType.Commercialist ||
                      activeUser.role?.name === RoleType.CommercialistII
                    )
                  }
                />
              )}
              <div className="date-is-notification-content">
                <div className="date">
                  <DateInput
                    id="expiryDate"
                    value={
                      newProductReservation.expiryDate
                        ? new Date(newProductReservation.expiryDate)
                        : null
                    }
                    onValueChange={handleNewProductReservationForm}
                    labelText={`${t("Krajnji datum rezervacije")} *`}
                    placeholderText={t("Datum")}
                    icon="bi bi-calendar-check"
                    control={control}
                    errors={errors}
                    disabled={
                      !(
                        activeUser.role?.name === RoleType.Superadmin ||
                        activeUser.role?.name === RoleType.Administrator ||
                        activeUser.role?.name === RoleType.CommercialistII
                      )
                    }
                  />
                </div>
                <div className="is-notification">
                  <RadioButtonInput
                    id="isNotification"
                    value={newProductReservation.isNotification as boolean}
                    radioButtonValue1={t("DA")}
                    radioButtonValue2={t("NE")}
                    onValueChange={handleNewProductReservationForm}
                    labelText={t("Obavesti me po isteku")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      !(
                        activeUser.role?.name === RoleType.Superadmin ||
                        activeUser.role?.name === RoleType.Administrator ||
                        activeUser.role?.name === RoleType.Commercialist ||
                        activeUser.role?.name === RoleType.CommercialistII
                      )
                    }
                  />
                </div>
              </div>
              {newProductReservation.type === ProductType.RawMaterial && (
                <div className="data-table-container">
                  <DataTable value={productsDataTable}>
                    <Column
                      body={nameOfProduct}
                      header={`${t("Naziv proizvoda")}:`}
                    ></Column>
                    <Column
                      field="amount"
                      header={`${t("Rezervisana količina")}:`}
                    ></Column>
                    <Column
                      field="amountRemain"
                      header={`${t("Nerealizovana količina")}:`}
                    ></Column>
                    <Column
                      body={measuringUnitOfProduct}
                      header={`${t("Merna jedinica")}:`}
                    ></Column>
                  </DataTable>
                </div>
              )}
              <div className="note-field">
                {newProductReservation.type === ProductType.MercantileGoods && (
                  <TextAreaInput
                    id="note"
                    value={newProductReservation.note as string}
                    onValueChange={handleNewProductReservationForm}
                    rows={2}
                    cols={50}
                    labelText={t("Napomena")}
                    placeholderText={t("Unesite napomenu")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      !(
                        activeUser.role?.name === RoleType.Superadmin ||
                        activeUser.role?.name === RoleType.Administrator ||
                        activeUser.role?.name === RoleType.Commercialist ||
                        activeUser.role?.name === RoleType.CommercialistII
                      )
                    }
                  />
                )}
              </div>
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
